import { TrackJS, TrackJSInstallOptions, TrackJSErrorPayload } from 'trackjs';
import { TrackJsEnvironmentConfig } from '../_interfaces/track-js-environment.interface';

const TRACK_JS_SESSION_ID_KEY = 'trackJsSessionId';

export function getOrCreateTrackJsSessionId(): string {
  const existing = sessionStorage.getItem(TRACK_JS_SESSION_ID_KEY);
  if (existing) {
    return existing;
  }

  const sessionId =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  sessionStorage.setItem(TRACK_JS_SESSION_ID_KEY, sessionId);
  return sessionId;
}

export function shouldCaptureTrackJsError(payload: TrackJSErrorPayload): boolean {
  const message = payload.message ?? '';

  // RxJS teardown artefact when subscriptions are unsubscribed during in-flight requests.
  return !message.includes('ObjectUnsubscribedError');
}

/**
 * Whether TrackJsHttpInterceptor should send this status to TrackJS.
 * - 0: browser/network failure (offline, CORS, aborted, DNS, etc.)
 * - 4xx/5xx: client and server errors from our API and third-party HTTP (maps, tiles, etc.)
 * Success (1xx–3xx) and other non-error statuses are never reported here; TrackJS native
 * network error capture stays off (`network.error: false` in install options) to avoid doubles.
 */
export function shouldReportHttpError(status: number): boolean {
  return status === 0 || status >= 400;
}

export function buildTrackJsInstallOptions(
  trackJsConfig: TrackJsEnvironmentConfig | undefined,
  customerIdentifier: string,
  appVersion: string
): TrackJSInstallOptions | null {
  if (!trackJsConfig?.token) {
    return null;
  }

  return {
    token: trackJsConfig.token,
    enabled: true,
    application: `water-utilities-${customerIdentifier}`,
    version: appVersion,
    sessionId: getOrCreateTrackJsSessionId(),
    console: {
      enabled: true,
      error: true,
      warn: false,
      display: true
    },
    network: {
      enabled: true,
      error: false
    },
    visitor: {
      enabled: true
    },
    callback: {
      enabled: true,
      bindStack: true
    },
    onError: shouldCaptureTrackJsError
  };
}

export function setTrackJsEnvironmentMetadata(
  customerIdentifier: string,
  production: boolean,
  appVersion: string
): void {
  TrackJS.addMetadata('customerIdentifier', customerIdentifier);
  TrackJS.addMetadata('production', production.toString());
  TrackJS.addMetadata('appVersion', appVersion);
}
